import { useIsFetching, useQuery } from "@tanstack/react-query"

import { BookingDetails } from "modules/BookingCommon/common/interfaces"
import { getBookingDetails, waitBookingStatusUpdate } from "modules/BookingCommon/common/api"
import { BookingStatus } from "utils/interfaces"
import { getPaidBookingDetails } from "modules/Booking/AfterPaymentPage/common"
import { Callbacks } from "modules/BookingCommon/common/queries/callbacks"
import { readSearchParams } from "utils/customHooks"
import { queryClient } from "utils/infrastructure/query-client"

export const BOOKING_DETAILS_QUERY_KEY = "bookingDetails"

function createKey(bookingHash?: string) {
    return [BOOKING_DETAILS_QUERY_KEY, bookingHash]
}

function useGetBookingDetails(bookingHash?: string) {
    return useQuery(
        createKey(bookingHash),
        () => {
            if (!bookingHash) return
            return getBookingDetails(bookingHash).then((r) => {
                bookingCreatedCallbacks.call(r)
                return r
            })
        },
        {
            enabled: !!bookingHash,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    )
}

function useIsBookingLoading() {
    return useIsFetching({ queryKey: [BOOKING_DETAILS_QUERY_KEY] })
}

function getBookingHashFromSearchParams() {
    return readSearchParams().get("booking_hash") ?? undefined
}

function setBookingDetailsQueryData(booking: BookingDetails) {
    queryClient.setQueryData(createKey(booking.booking_hash), booking)
}

function getBookingDetailsQueryData(bookingHash: string) {
    return queryClient.getQueryData<BookingDetails>(createKey(bookingHash))
}

function updateBookingDetailsQueryData(bookingHash: string, booking: Partial<BookingDetails>) {
    const data = getBookingDetailsQueryData(bookingHash)
    if (!data) return
    setBookingDetailsQueryData({ ...data, ...booking })
}

const bookingCreatedCallbacks: Callbacks<BookingDetails> = {
    callbacks: [],
    call: (data) => {
        while (bookingCreatedCallbacks.callbacks.length) {
            bookingCreatedCallbacks.callbacks.pop()?.(data)
        }
    },
    add: (fn) => {
        bookingCreatedCallbacks.callbacks.push(fn)
    },
}

function useGetUpdatedBookingDetails(bookingHash?: string, currentStatus?: BookingStatus) {
    return useQuery(
        [BOOKING_DETAILS_QUERY_KEY, bookingHash, currentStatus],
        () => {
            if (!bookingHash || !currentStatus) return
            if (currentStatus === "pay_started" || currentStatus === "not_paid") {
                return waitBookingStatusUpdate(bookingHash, currentStatus).then((result) => {
                    createdBookingLoader.setQueryData(result)
                    return result
                })
            }
            return getBookingDetailsQueryData(bookingHash)
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!bookingHash && !!currentStatus,
        }
    )
}

export const createdBookingLoader = {
    useGet: useGetBookingDetails,
    useIsLoading: useIsBookingLoading,
    getHash: getBookingHashFromSearchParams,
    useGetUpdated: useGetUpdatedBookingDetails,
    setQueryData: setBookingDetailsQueryData,
    getQueryData: getBookingDetailsQueryData,
    updateData: updateBookingDetailsQueryData,
    onSuccess: bookingCreatedCallbacks.add,
}

function useGetPaidBookingDetails(bookingHash?: string) {
    return useQuery(
        ["paidBookingDetails", bookingHash],
        () => {
            if (!bookingHash) return
            return getPaidBookingDetails(bookingHash)
        },
        {
            enabled: !!bookingHash,
            refetchOnWindowFocus: false,
        }
    )
}

export const paidBookingLoader = {
    useGet: useGetPaidBookingDetails,
}
